import React from 'react';
import {
  VideoDisabledView,
  VideoHiddenView,
  VideoConnectingView,
  ClickStartSessionView,
  SmallConnectingView,
  SmallStartSessionView
} from './OverlayViews';
import { CountryFlag } from '../CountryFlag';
import { CountryInfo } from '@/services/geolocation';

type VideoType = 'local' | 'remote';
type PeerVideoStatus = 'waiting' | 'connecting' | 'reconnecting' | 'connected' | 'disconnected' | 'error';

interface VideoOverlaysProps {
  videoType: VideoType;
  state: {
    hasStream: boolean;
    sessionActive: boolean;
    peerVideoStatus?: PeerVideoStatus;
    isVideoEnabled?: boolean;
    isPeerVideoHidden?: boolean;
    peerVideoEnabled?: boolean;
    rtcConnectionState?: string;
    peerAudioEnabled?: boolean;
    swapPositions?: boolean;
    userCountry?: CountryInfo | null;
    peerCountry?: CountryInfo | null;
  };
  size?: 'full' | 'small';
}

/**
 * Declaratively renders video overlays based on state
 * Similar to ErrorMessageOverlay pattern - takes state and renders appropriate UI
 */
export const VideoOverlays: React.FC<VideoOverlaysProps> = ({
  videoType,
  state,
  size = 'full'
}) => {
  const {
    hasStream,
    sessionActive,
    peerVideoStatus,
    isVideoEnabled,
    isPeerVideoHidden,
    peerVideoEnabled,
    rtcConnectionState,
    peerAudioEnabled,
    swapPositions,
    userCountry,
    peerCountry
  } = state;

  // Local video overlays
  if (videoType === 'local') {
    if (hasStream && !isVideoEnabled) {
      return <VideoDisabledView />;
    }
    if (!hasStream) {
      return size === 'small' ? <SmallStartSessionView /> : <ClickStartSessionView />;
    }
    return null;
  }

  // Remote video overlays
  if (videoType === 'remote') {
    const showCountryFlag = sessionActive && rtcConnectionState === 'connected' && peerCountry;
    const audioMuteIndicator = null; // Currently not used

    return (
      <>
        {sessionActive && rtcConnectionState === 'connected' && isPeerVideoHidden && <VideoHiddenView />}
        {sessionActive && rtcConnectionState === 'connected' && !isPeerVideoHidden && !peerVideoEnabled && <VideoDisabledView />}
        {sessionActive && rtcConnectionState !== 'connected' && (
          size === 'small' ? <SmallConnectingView /> : <VideoConnectingView />
        )}
        {!sessionActive && (
          size === 'small' ? <SmallStartSessionView /> : <ClickStartSessionView />
        )}
        {showCountryFlag && (
          <div className={size === 'small' ? "absolute bottom-1 left-1" : "absolute bottom-2 left-2"}>
            <CountryFlag country={peerCountry} size="sm"/>
          </div>
        )}
        {sessionActive && rtcConnectionState === 'connected' && !peerAudioEnabled && audioMuteIndicator}
      </>
    );
  }

  return null;
};
